import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    FileText, Upload, FileDown, Trash2, FilePlus2, Loader2, Sparkles,
    CheckCircle2, AlertTriangle, BrainCircuit,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DOC_TAXONOMIA, labelTipo } from "../../lib/catalogs";
import { ROLE_LABELS } from "../../lib/roles";

export default function TabDocumental({ fideicomisoId, fideicomiso, onReload }) {
    const [docs, setDocs] = useState([]);
    const [plantillas, setPlantillas] = useState([]);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [tplOpen, setTplOpen] = useState(false);
    const [extractions, setExtractions] = useState({});
    const [analyzing, setAnalyzing] = useState({});

    const AI_TIPOS = new Set([
        "identificacion_oficial",
        "comprobante_domicilio",
        "acta_constitutiva",
        "kyc_identificacion",
        "kyc_domicilio",
        "constitucion",
    ]);

    const load = async () => {
        const [{ data: d }, { data: t }] = await Promise.all([
            api.get(`/fideicomisos/${fideicomisoId}/documentos`),
            api.get("/plantillas"),
        ]);
        setDocs(d);
        setPlantillas(t);
        // fetch extractions per doc that qualifies (best-effort)
        const results = {};
        await Promise.all(
            d.filter((x) => AI_TIPOS.has(x.tipo_taxonomia)).map(async (x) => {
                try {
                    const { data: ex } = await api.get(`/documentos/${x.id}/extraction`);
                    results[x.id] = ex;
                } catch { /* 404 = sin extraction */ }
            }),
        );
        setExtractions(results);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fideicomisoId]);

    const analyzeIA = async (docId) => {
        setAnalyzing((a) => ({ ...a, [docId]: true }));
        try {
            const { data: ex } = await api.post(`/documentos/${docId}/analizar-ia`, {
                aplicar_automaticamente: false,
            });
            setExtractions((e) => ({ ...e, [docId]: ex }));
            const conf = Math.round((ex.confianza_clasificacion || 0) * 100);
            if (ex.hil_queue_item) {
                toast.info(`IA · ${ex.tipo_documento_detectado} · ${conf}% · enviado a HIL`);
            } else {
                toast.success(`IA · ${ex.tipo_documento_detectado} · ${conf}% confianza`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error IA");
        } finally {
            setAnalyzing((a) => ({ ...a, [docId]: false }));
        }
    };

    const download = async (docId, filename) => {
        try {
            const res = await api.get(`/documentos/${docId}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        }
    };

    return (
        <div className="space-y-6 pb-16">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="font-display text-2xl">Repositorio documental</h2>
                    <p className="text-sm text-muted-foreground">
                        Archivos, versiones y plantillas del expediente.
                    </p>
                </div>
                <div className="flex gap-2">
                    <PlantillaDialog
                        open={tplOpen}
                        setOpen={setTplOpen}
                        plantillas={plantillas}
                        fideicomisoId={fideicomisoId}
                        onDone={() => {
                            load();
                            onReload?.();
                        }}
                    />
                    <UploadDialog
                        open={uploadOpen}
                        setOpen={setUploadOpen}
                        fideicomisoId={fideicomisoId}
                        onDone={() => {
                            load();
                            onReload?.();
                        }}
                    />
                </div>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="documentos-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Tipo</th>
                            <th>Versión</th>
                            <th>Plantilla</th>
                            <th>Hash</th>
                            <th>Subido por</th>
                            <th>Permisos</th>
                            <th>Fecha</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {docs.map((d) => (
                            <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                                <td className="font-medium text-sm flex items-center gap-2">
                                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                                    {d.nombre}
                                </td>
                                <td>
                                    <span className="badge-gold">
                                        {labelTipo(d.tipo_taxonomia, DOC_TAXONOMIA)}
                                    </span>
                                </td>
                                <td className="tabular text-xs">v{d.version}</td>
                                <td className="text-xs">{d.plantilla_codigo || "—"}</td>
                                <td className="hash-cell">{d.hash?.slice(0, 10)}…</td>
                                <td className="text-xs">{d.uploaded_by}</td>
                                <td className="text-xs">
                                    {(d.permisos_roles?.length ?? 0) === 0 ? (
                                        <span className="text-muted-foreground">todos</span>
                                    ) : (
                                        d.permisos_roles.map((r) => (
                                            <span key={r} className="badge-gold text-[9px] mr-1">
                                                {ROLE_LABELS[r] || r}
                                            </span>
                                        ))
                                    )}
                                </td>
                                <td className="text-[11px] tabular">
                                    {d.uploaded_at?.slice(0, 16).replace("T", " ")}
                                </td>
                                <td>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => download(d.id, d.filename)}
                                            data-testid={`download-${d.id}`}
                                        >
                                            <FileDown className="h-3 w-3" />
                                        </Button>
                                        {AI_TIPOS.has(d.tipo_taxonomia) && (
                                            extractions[d.id] ? (
                                                extractions[d.id].hil_queue_item ? (
                                                    <Link to={`/ia/hil/${extractions[d.id].hil_queue_item.id}`}
                                                        title="Ver revisión HIL"
                                                        data-testid={`hil-link-${d.id}`}>
                                                        <Button size="sm" variant="outline" className="h-7 px-2">
                                                            <BrainCircuit className="h-3 w-3 mr-1" />
                                                            HIL
                                                        </Button>
                                                    </Link>
                                                ) : (
                                                    <span
                                                        className="badge-exito text-[10px]"
                                                        title={`${Math.round((extractions[d.id].confianza_clasificacion || 0) * 100)}% confianza`}
                                                        data-testid={`extraction-badge-${d.id}`}
                                                    >
                                                        <CheckCircle2 className="h-3 w-3" /> IA {Math.round((extractions[d.id].confianza_clasificacion || 0) * 100)}%
                                                    </span>
                                                )
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-7 px-2 text-[10px]"
                                                    onClick={() => analyzeIA(d.id)}
                                                    disabled={analyzing[d.id]}
                                                    data-testid={`analizar-ia-btn-${d.id}`}
                                                >
                                                    {analyzing[d.id] ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <Sparkles className="h-3 w-3 mr-1" /> IA
                                                        </>
                                                    )}
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {docs.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center py-10 text-muted-foreground">
                                    Sin documentos. Sube el primero o genera desde plantilla.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function UploadDialog({ open, setOpen, fideicomisoId, onDone }) {
    const [form, setForm] = useState({ tipo_taxonomia: "contrato", nombre: "", permisos_roles: "" });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!file) return toast.error("Selecciona archivo");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("tipo_taxonomia", form.tipo_taxonomia);
            fd.append("nombre", form.nombre);
            fd.append("permisos_roles", form.permisos_roles);
            fd.append("archivo", file);
            await api.post(`/fideicomisos/${fideicomisoId}/documentos`, fd);
            toast.success("Documento subido");
            setForm({ tipo_taxonomia: "contrato", nombre: "", permisos_roles: "" });
            setFile(null);
            setOpen(false);
            onDone?.();
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" data-testid="upload-doc-btn">
                    <Upload className="h-4 w-4 mr-2" /> Subir documento
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Subir documento</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="upload-doc-form">
                    <div>
                        <Label>Nombre</Label>
                        <Input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="doc-nombre" />
                    </div>
                    <div>
                        <Label>Tipo</Label>
                        <Select
                            value={form.tipo_taxonomia}
                            onValueChange={(v) => setForm({ ...form, tipo_taxonomia: v })}
                        >
                            <SelectTrigger data-testid="doc-tipo"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {DOC_TAXONOMIA.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Permisos (roles separados por coma; vacío = todos)</Label>
                        <Input
                            placeholder="direccion,auditor"
                            value={form.permisos_roles}
                            onChange={(e) => setForm({ ...form, permisos_roles: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>Archivo</Label>
                        <Input type="file" required onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file" />
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={busy} data-testid="submit-upload-doc">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subir"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function PlantillaDialog({ open, setOpen, plantillas, fideicomisoId, onDone }) {
    const [codigo, setCodigo] = useState("");
    const [tpl, setTpl] = useState(null);
    const [valores, setValores] = useState({});
    const [nombre, setNombre] = useState("");
    const [busy, setBusy] = useState(false);

    const loadTpl = async (c) => {
        setCodigo(c);
        try {
            const { data } = await api.get(`/plantillas/${c}`);
            setTpl(data);
            setNombre(data.nombre);
            setValores({});
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/fideicomisos/${fideicomisoId}/documentos/desde-plantilla`, {
                codigo, valores, nombre_documento: nombre, permisos_roles: [],
            });
            toast.success("Documento generado desde plantilla");
            setOpen(false);
            setCodigo(""); setTpl(null); setValores({}); setNombre("");
            onDone?.();
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="new-from-template-btn">
                    <FilePlus2 className="h-4 w-4 mr-2" /> Nuevo desde plantilla
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Generar documento desde plantilla</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Plantilla</Label>
                        <Select value={codigo} onValueChange={loadTpl}>
                            <SelectTrigger data-testid="template-select"><SelectValue placeholder="Selecciona plantilla…" /></SelectTrigger>
                            <SelectContent className="max-h-80">
                                {plantillas.map((p) => (
                                    <SelectItem key={p.codigo} value={p.codigo}>
                                        {p.codigo} · {p.nombre}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {tpl && (
                        <form onSubmit={submit} className="space-y-3">
                            <div>
                                <Label>Nombre del documento</Label>
                                <Input required value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="tpl-nombre-doc" />
                            </div>
                            <div className="border-t border-border pt-3">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                                    Campos de {tpl.codigo}
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                    {tpl.campos.map((c) => (
                                        <FieldInput
                                            key={c.key}
                                            def={c}
                                            value={valores[c.key] || ""}
                                            onChange={(v) => setValores({ ...valores, [c.key]: v })}
                                        />
                                    ))}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={busy} data-testid="submit-tpl">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generar PDF"}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function FieldInput({ def, value, onChange }) {
    const label = def.label + (def.required ? " *" : "");
    if (def.tipo === "textarea") {
        return (
            <div className="md:col-span-2">
                <Label className="text-xs">{label}</Label>
                <Textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`tpl-field-${def.key}`} />
            </div>
        );
    }
    if (def.tipo === "select") {
        return (
            <div>
                <Label className="text-xs">{label}</Label>
                <Select value={value || undefined} onValueChange={onChange}>
                    <SelectTrigger data-testid={`tpl-field-${def.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                        {def.opciones?.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }
    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <Input
                type={def.tipo === "number" ? "number" : def.tipo === "date" ? "date" : "text"}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={`tpl-field-${def.key}`}
            />
        </div>
    );
}
